import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslate } from "@refinedev/core";
import { Show } from "@refinedev/mui";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  Stack,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
  Snackbar,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import {
  ArrowBack,
  Groups,
  SwapHoriz,
  PersonOff,
  CheckCircle,
  Info,
} from "@mui/icons-material";
import { DragDropContext, DropResult } from "@hello-pangea/dnd";
import axiosInstance from "../../api/axios-config";
import { useAuth } from "../../contexts/auth-context";
import { useSubGroups, SubGroup } from "../../hooks/useSubGroups";
import { DnDUserColumn } from "../../components/sub-groups/DnDUserColumn";
import { SubGroupSelector } from "../../components/sub-groups/SubGroupSelector";
import { DnDUser } from "../../components/sub-groups/DnDUserCard";

interface SnackbarState {
  open: boolean;
  message: string;
  severity: "success" | "error" | "info";
}

export const SubGroupMembers: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const translate = useTranslate();
  const theme = useTheme();
  const { user: currentUser } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Get enterprise ID from the sub-group
  const [enterpriseId, setEnterpriseId] = useState<number | null>(null);

  // Use the sub-groups hook once we have the enterprise ID
  const { subGroups, loading: subGroupsLoading } = useSubGroups(enterpriseId || undefined);

  // State
  const [selectedSubGroupId, setSelectedSubGroupId] = useState<number | null>(
    id ? Number(id) : null
  );
  const [unassignedUsers, setUnassignedUsers] = useState<DnDUser[]>([]);
  const [assignedUsers, setAssignedUsers] = useState<DnDUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Search states
  const [unassignedSearch, setUnassignedSearch] = useState("");
  const [assignedSearch, setAssignedSearch] = useState("");

  // Selection states (using Set for O(1) lookups)
  const [selectedUnassigned, setSelectedUnassigned] = useState<Set<number>>(new Set());
  const [selectedAssigned, setSelectedAssigned] = useState<Set<number>>(new Set());

  // Snackbar state
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: "",
    severity: "success",
  });

  // Action loading state
  const [actionLoading, setActionLoading] = useState(false);

  // Fetch sub-group details and set enterprise ID
  const fetchSubGroupDetails = useCallback(async () => {
    if (!id) return;
    try {
      const response = await axiosInstance.get<SubGroup>(`/sub-groups/${id}`);
      setEnterpriseId(response.data.entrepriseId);
    } catch (err: any) {
      console.error("Failed to fetch sub-group:", err);
      setError(err?.response?.data?.message || "Erreur lors du chargement du sous-groupe");
    }
  }, [id]);

  // Fetch users assignable to the selected sub-group
  const fetchUsers = useCallback(async () => {
    if (!selectedSubGroupId) return;

    try {
      const response = await axiosInstance.get(`/sub-groups/${selectedSubGroupId}/available-users`);
      let usersData: DnDUser[] = [];

      if (Array.isArray(response.data)) {
        usersData = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        usersData = response.data.data;
      }

      // Map users: ensure subGroupId is correctly set
      const mappedUsers = usersData.map((u: any) => ({
        ...u,
        subGroupId: u.subGroupId ?? u.subGroup?.id ?? null,
      }));

      // Split users: unassigned (no sub-group) vs assigned (in selected sub-group)
      const unassigned = mappedUsers.filter((u) => u.subGroupId === null);
      const assigned = mappedUsers.filter((u) => u.subGroupId === selectedSubGroupId);

      setUnassignedUsers(unassigned);
      setAssignedUsers(assigned);
    } catch (err: any) {
      console.error("Failed to fetch users:", err);
      setError(err?.response?.data?.message || "Erreur lors du chargement des utilisateurs");
    }
  }, [selectedSubGroupId]);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchSubGroupDetails();
    };
    loadData();
  }, [fetchSubGroupDetails]);

  // Fetch users when selected sub-group changes
  useEffect(() => {
    if (selectedSubGroupId) {
      const loadUsers = async () => {
        setLoading(true);
        await fetchUsers();
        setLoading(false);
      };
      loadUsers();
    }
  }, [selectedSubGroupId, fetchUsers]);

  // Clear selections when sub-group changes
  useEffect(() => {
    setSelectedUnassigned(new Set());
    setSelectedAssigned(new Set());
    setUnassignedSearch("");
    setAssignedSearch("");
  }, [selectedSubGroupId]);

  // Get current sub-group details
  const currentSubGroup = useMemo(() => {
    return subGroups.find((sg) => sg.id === selectedSubGroupId);
  }, [subGroups, selectedSubGroupId]);

  // Handle user selection with Ctrl/Shift support
  const handleSelectUser = useCallback(
    (
      userId: number,
      event: React.MouseEvent,
      column: "unassigned" | "assigned"
    ) => {
      const setSelected = column === "unassigned" ? setSelectedUnassigned : setSelectedAssigned;
      const users = column === "unassigned" ? unassignedUsers : assignedUsers;

      setSelected((prev) => {
        const newSet = new Set(prev);

        if (event.ctrlKey || event.metaKey) {
          // Toggle individual selection
          if (newSet.has(userId)) {
            newSet.delete(userId);
          } else {
            newSet.add(userId);
          }
        } else if (event.shiftKey && prev.size > 0) {
          // Range selection
          const lastSelected = Array.from(prev).pop();
          const currentIndex = users.findIndex((u) => u.id === userId);
          const lastIndex = users.findIndex((u) => u.id === lastSelected);

          if (currentIndex !== -1 && lastIndex !== -1) {
            const [start, end] = [
              Math.min(currentIndex, lastIndex),
              Math.max(currentIndex, lastIndex),
            ];
            for (let i = start; i <= end; i++) {
              newSet.add(users[i].id);
            }
          }
        } else {
          // Single selection (clear others)
          newSet.clear();
          newSet.add(userId);
        }

        return newSet;
      });

      // Clear selection in the other column
      if (column === "unassigned") {
        setSelectedAssigned(new Set());
      } else {
        setSelectedUnassigned(new Set());
      }
    },
    [unassignedUsers, assignedUsers]
  );

  // Show notification
  const showSnackbar = (message: string, severity: SnackbarState["severity"]) => {
    setSnackbar({ open: true, message, severity });
  };

  // API calls for member management
  const addMembersToSubGroup = async (userIds: number[]) => {
    if (!selectedSubGroupId || userIds.length === 0) return;

    try {
      await Promise.all(
        userIds.map((userId) =>
          axiosInstance.post(`/sub-groups/${selectedSubGroupId}/members/${userId}`)
        )
      );
      return true;
    } catch (err: any) {
      console.error("Failed to add members:", err);
      throw err;
    }
  };

  const removeMembersFromSubGroup = async (userIds: number[]) => {
    if (!selectedSubGroupId || userIds.length === 0) return;

    try {
      await Promise.all(
        userIds.map((userId) =>
          axiosInstance.delete(`/sub-groups/${selectedSubGroupId}/members/${userId}`)
        )
      );
      return true;
    } catch (err: any) {
      console.error("Failed to remove members:", err);
      throw err;
    }
  };

  // Handle promote user to SUB_MODERATOR
  const handlePromote = useCallback(
    async (userId: number) => {
      if (!selectedSubGroupId) return;

      try {
        await axiosInstance.patch(`/sub-groups/${selectedSubGroupId}/moderator/${userId}`);
        // Update user role in local state
        setAssignedUsers((prev) =>
          prev.map((u) => (u.id === userId ? { ...u, role: "SUB_MODERATOR" } : u))
        );
        showSnackbar(
          translate("subGroups.promoteSuccess", "Utilisateur promu responsable"),
          "success"
        );
      } catch (err: any) {
        console.error("Failed to promote user:", err);
        showSnackbar(
          err?.response?.data?.message ||
            translate("errors.generic", "Une erreur est survenue"),
          "error"
        );
      }
    },
    [selectedSubGroupId, translate]
  );

  // Handle demote user from SUB_MODERATOR
  const handleDemote = useCallback(
    async (userId: number) => {
      if (!selectedSubGroupId) return;

      try {
        await axiosInstance.delete(`/sub-groups/${selectedSubGroupId}/moderator/${userId}`);
        // Update user role in local state
        setAssignedUsers((prev) =>
          prev.map((u) => (u.id === userId ? { ...u, role: "USER" } : u))
        );
        showSnackbar(
          translate("subGroups.demoteSuccess", "Rôle de responsable retiré"),
          "success"
        );
      } catch (err: any) {
        console.error("Failed to demote user:", err);
        showSnackbar(
          err?.response?.data?.message ||
            translate("errors.generic", "Une erreur est survenue"),
          "error"
        );
      }
    },
    [selectedSubGroupId, translate]
  );

  // Handle drag end
  const handleDragEnd = useCallback(
    async (result: DropResult) => {
      const { source, destination, draggableId } = result;

      // Dropped outside a valid zone
      if (!destination) return;

      // No sub-group selected
      if (!selectedSubGroupId) {
        showSnackbar(
          translate("subGroups.dnd.selectGroup", "Veuillez sélectionner un sous-groupe"),
          "error"
        );
        return;
      }

      // Same column, same position = no change
      if (
        source.droppableId === destination.droppableId &&
        source.index === destination.index
      ) {
        return;
      }

      // Extract user ID from draggableId
      const draggedUserId = parseInt(draggableId.replace("user-", ""), 10);

      // Determine which users to move (all selected if dragged item is selected)
      let usersToMove: DnDUser[] = [];

      if (source.droppableId === "unassigned") {
        // Moving from unassigned to assigned
        if (selectedUnassigned.has(draggedUserId) && selectedUnassigned.size > 1) {
          // Multi-select drag
          usersToMove = unassignedUsers.filter((u) => selectedUnassigned.has(u.id));
        } else {
          // Single drag
          usersToMove = unassignedUsers.filter((u) => u.id === draggedUserId);
        }
      } else {
        // Moving from assigned to unassigned
        if (selectedAssigned.has(draggedUserId) && selectedAssigned.size > 1) {
          // Multi-select drag
          usersToMove = assignedUsers.filter((u) => selectedAssigned.has(u.id));
        } else {
          // Single drag
          usersToMove = assignedUsers.filter((u) => u.id === draggedUserId);
        }
      }

      if (usersToMove.length === 0) return;

      const userIds = usersToMove.map((u) => u.id);

      // Optimistic update
      if (source.droppableId === "unassigned" && destination.droppableId === "assigned") {
        // Add to sub-group
        setUnassignedUsers((prev) => prev.filter((u) => !userIds.includes(u.id)));
        setAssignedUsers((prev) => [
          ...prev,
          ...usersToMove.map((u) => ({ ...u, subGroupId: selectedSubGroupId })),
        ]);
        setSelectedUnassigned(new Set());

        setActionLoading(true);
        try {
          await addMembersToSubGroup(userIds);
          showSnackbar(
            translate("subGroups.dnd.successAssign", `${userIds.length} utilisateur(s) assigné(s)`)
              .replace("{count}", String(userIds.length)),
            "success"
          );
        } catch (err: any) {
          // Rollback on error
          setUnassignedUsers((prev) => [...prev, ...usersToMove]);
          setAssignedUsers((prev) => prev.filter((u) => !userIds.includes(u.id)));
          showSnackbar(
            err?.response?.data?.message ||
              translate("errors.generic", "Une erreur est survenue"),
            "error"
          );
        } finally {
          setActionLoading(false);
        }
      } else if (source.droppableId === "assigned" && destination.droppableId === "unassigned") {
        // Remove from sub-group
        setAssignedUsers((prev) => prev.filter((u) => !userIds.includes(u.id)));
        setUnassignedUsers((prev) => [
          ...prev,
          ...usersToMove.map((u) => ({ ...u, subGroupId: null })),
        ]);
        setSelectedAssigned(new Set());

        setActionLoading(true);
        try {
          await removeMembersFromSubGroup(userIds);
          showSnackbar(
            translate("subGroups.dnd.successUnassign", `${userIds.length} utilisateur(s) retiré(s)`)
              .replace("{count}", String(userIds.length)),
            "success"
          );
        } catch (err: any) {
          // Rollback on error
          setAssignedUsers((prev) => [...prev, ...usersToMove]);
          setUnassignedUsers((prev) => prev.filter((u) => !userIds.includes(u.id)));
          showSnackbar(
            err?.response?.data?.message ||
              translate("errors.generic", "Une erreur est survenue"),
            "error"
          );
        } finally {
          setActionLoading(false);
        }
      }
    },
    [
      selectedSubGroupId,
      selectedUnassigned,
      selectedAssigned,
      unassignedUsers,
      assignedUsers,
      translate,
    ]
  );

  // Handle sub-group change from URL parameter
  useEffect(() => {
    if (id && Number(id) !== selectedSubGroupId) {
      setSelectedSubGroupId(Number(id));
    }
  }, [id]);

  // Update URL when sub-group changes
  const handleSubGroupChange = (newSubGroupId: number | null) => {
    setSelectedSubGroupId(newSubGroupId);
    if (newSubGroupId) {
      navigate(`/company/sub-groups/${newSubGroupId}/members`, { replace: true });
    }
  };

  if (loading && !enterpriseId) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => navigate(-1)} sx={{ mt: 2 }} startIcon={<ArrowBack />}>
          {translate("buttons.back", "Retour")}
        </Button>
      </Box>
    );
  }

  return (
    <Show
      headerButtons={false}
      headerProps={{ sx: { display: "none" } }}
      breadcrumb={false}
      goBack={false}
      title={false}
    >
      {/* Header */}
      <Card
        sx={{
          mb: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`,
        }}
      >
        <CardContent sx={{ py: 3 }}>
          <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
            <IconButton
              onClick={() => navigate(-1)}
              sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}
            >
              <ArrowBack />
            </IconButton>

            <Avatar
              sx={{
                width: 56,
                height: 56,
                bgcolor: theme.palette.primary.main,
              }}
            >
              <Groups sx={{ fontSize: 28 }} />
            </Avatar>

            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography variant="h5" fontWeight={700} color="primary.main">
                {translate("subGroups.dnd.title", "Gestion des membres")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {translate(
                  "subGroups.dnd.dragHint",
                  "Glissez les utilisateurs pour les assigner ou les retirer"
                )}
              </Typography>
            </Box>

            {/* Sub-group selector */}
            <Box sx={{ minWidth: isMobile ? "100%" : 300, mt: isMobile ? 2 : 0 }}>
              <SubGroupSelector
                subGroups={subGroups}
                selectedSubGroupId={selectedSubGroupId}
                onSubGroupChange={handleSubGroupChange}
                disabled={subGroupsLoading}
              />
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Hint card */}
      <Alert
        severity="info"
        icon={<Info />}
        sx={{
          mb: 3,
          bgcolor: alpha(theme.palette.info.main, 0.05),
          "& .MuiAlert-icon": {
            color: theme.palette.info.main,
          },
        }}
      >
        <Typography variant="body2">
          <strong>{translate("subGroups.dnd.multiSelectHint", "Astuce")}:</strong>{" "}
          {translate(
            "subGroups.dnd.multiSelectDesc",
            "Utilisez Ctrl+clic pour sélectionner plusieurs utilisateurs, puis glissez-les ensemble."
          )}
        </Typography>
      </Alert>

      {/* Main content - Two columns */}
      <Card
        sx={{
          minHeight: 500,
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        {/* Loading overlay */}
        {(loading || actionLoading) && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: alpha(theme.palette.background.paper, 0.7),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
              borderRadius: 2,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        <DragDropContext onDragEnd={handleDragEnd}>
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              flex: 1,
              minHeight: 0,
            }}
          >
            {/* Left column - Unassigned users */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                borderRight: isMobile ? "none" : `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                borderBottom: isMobile ? `1px solid ${alpha(theme.palette.divider, 0.3)}` : "none",
                minHeight: isMobile ? 300 : "auto",
              }}
            >
              <DnDUserColumn
                droppableId="unassigned"
                title={translate("subGroups.dnd.unassignedColumn", "Utilisateurs non assignés")}
                users={unassignedUsers}
                searchQuery={unassignedSearch}
                onSearchChange={setUnassignedSearch}
                selectedUserIds={selectedUnassigned}
                onSelectUser={(userId, event) => handleSelectUser(userId, event, "unassigned")}
                emptyMessage={translate(
                  "subGroups.dnd.emptyUnassigned",
                  "Tous les utilisateurs sont assignés à un sous-groupe"
                )}
                emptyIcon={<CheckCircle sx={{ fontSize: 48, opacity: 0.3, mb: 1, color: "success.main" }} />}
                highlightColor="warning"
              />
            </Box>

            {/* Center divider with arrow */}
            {!isMobile && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 40,
                  bgcolor: alpha(theme.palette.divider, 0.05),
                }}
              >
                <Tooltip title={translate("subGroups.dnd.dragBetween", "Glissez entre les colonnes")}>
                  <SwapHoriz sx={{ color: "text.disabled", fontSize: 28 }} />
                </Tooltip>
              </Box>
            )}

            {/* Right column - Assigned users */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                minHeight: isMobile ? 300 : "auto",
              }}
            >
              <DnDUserColumn
                droppableId="assigned"
                title={
                  currentSubGroup
                    ? `${translate("subGroups.dnd.assignedColumn", "Membres de")} ${currentSubGroup.name}`
                    : translate("subGroups.dnd.noGroupSelected", "Aucun groupe sélectionné")
                }
                users={assignedUsers}
                searchQuery={assignedSearch}
                onSearchChange={setAssignedSearch}
                selectedUserIds={selectedAssigned}
                onSelectUser={(userId, event) => handleSelectUser(userId, event, "assigned")}
                emptyMessage={
                  selectedSubGroupId
                    ? translate("subGroups.dnd.emptyAssigned", "Aucun membre dans ce groupe")
                    : translate("subGroups.dnd.selectGroupFirst", "Sélectionnez un sous-groupe")
                }
                emptyIcon={
                  selectedSubGroupId ? (
                    <PersonOff sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                  ) : (
                    <Groups sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                  )
                }
                isDragDisabled={!selectedSubGroupId}
                highlightColor="success"
                onPromote={handlePromote}
                onDemote={handleDemote}
                showRoleActions={!!selectedSubGroupId}
              />
            </Box>
          </Box>
        </DragDropContext>
      </Card>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Show>
  );
};

export default SubGroupMembers;
